import { DefaultAccountingCollectionGroup } from '../../Models/AccountingCollectionGroup';
import { CreatedSubmissionRequest, PortalTextCommonImport, ShareableLink, SubmissionDetail } from '../../Models/Api/strongbox.financialportal';
import {
    PortalCreateSubmissionRequestOptions,
    PortalDefaultBasisOfAccounting,
    PortalSubmissionRequestDefaultFullFiscalYears
} from '../../Models/PortalCreateSubmissionRequestOptions';
import { ConnectionDescriptor } from '../../Models/Api/strongbox.financialportal';
import { LogMessage, SeverityLevel } from '../../Utils/Logging';
import { ApplicationState } from '../index';
import { ConnectorType } from '../ImportFinancials';
import { AccountingSystemConnectionState, ExistingConnectionLookupState, IConnectionError, ICurrentSubmission, IDataCollectionSubmissionRequest, SubmissionState } from './Actions';
import { NewDataCollectionContentGrouping } from './Reducer';

export function GetCreationOptions(state: ApplicationState, commonImportText?: PortalTextCommonImport): PortalCreateSubmissionRequestOptions {
    if (!!(state && state.newDataCollectionParameters && state.newDataCollectionParameters.creationOptions)) {
        return state.newDataCollectionParameters.creationOptions;
    }

    // This really is a stopgap.  There shouldn't be a scenario where we have to fall back to this.

    const msg = 'Unexpected error in NewDataCollectionParameters::GetCreationOptions, store value is undefined. Returning reasonable defaults';
    LogMessage(msg, SeverityLevel.Error);
    console.error(msg);

    const today = new Date();
    return {
        basisOfAccountingPreference: PortalDefaultBasisOfAccounting,
        fullFiscalYearsToCollect: PortalSubmissionRequestDefaultFullFiscalYears,
        anonymizeCustomersAndVendors: true,
        provideUserCopy: false,
        allowUserUpload: false,
        endOfImport: 'Today',
        accountingCollectionGroups: DefaultAccountingCollectionGroup(commonImportText),
        mostRecentPeriodMonth: today.getMonth(),
        mostRecentPeriodYear: today.getFullYear(),
        mostRecentPeriodDay: today.getDate(),
        hasShareableLink: false,
    };
}

export function GetRequestedOptions(state: ApplicationState): PortalCreateSubmissionRequestOptions | undefined {
    return state && state.newDataCollectionParameters && state.newDataCollectionParameters.requestedOptions;
}

export function GetRequestedOptionsId(state: ApplicationState): number | undefined {
    return state && state.newDataCollectionParameters && state.newDataCollectionParameters.requestedOptionsId;
}

export function RetrievingShareableLinkOptions(state: ApplicationState): boolean {
    return state && state.newDataCollectionParameters && state.newDataCollectionParameters.retrieving;
}

export function RetrieveShareableLinkOptionsFailed(state: ApplicationState): boolean {
    return !!state && !!state.newDataCollectionParameters && !!state.newDataCollectionParameters.initializationException;
}

export function GetCollectionGroupings(state: ApplicationState): NewDataCollectionContentGrouping[] {
    return (!!state && !!state.newDataCollectionParameters && state.newDataCollectionParameters.newDataCollectionGroupings) || [];
}

function _getCurrentSubmission(state: ApplicationState): ICurrentSubmission {
    return state?.newDataCollectionParameters?.currentSubmission
}

export function GetDataCollectionSubmissionsCount(state: ApplicationState): number {
    return state?.newDataCollectionParameters?.dataCollectionSubmissionsCount ?? 0;
}

export function ShowShareLinkLoadingModal(state: ApplicationState): boolean {
    return _getCurrentSubmission(state)?.submissionState === SubmissionState.creatingRequest;
}

export function GetCurrentDataCollectionSubmissionRequest(state: ApplicationState): IDataCollectionSubmissionRequest | undefined {
    return _getCurrentSubmission(state)?.dataCollectionSubmissionRequest;
}

export function GetCurrentSubmissionRequest(state: ApplicationState): CreatedSubmissionRequest | null {
    return _getCurrentSubmission(state)?.submissionRequest
}

export function ShowShareLinkModal(state: ApplicationState): boolean {
    return GetCurrentSubmissionRequest(state)?.hasShareableLink ?? false;
}

export function GetShareLinkInfo(state: ApplicationState): ShareableLink | null {
    return GetCurrentSubmissionRequest(state)?.shareableLink ?? null;
}

export function GetShowConnectToAccountingSystemModal(state: ApplicationState): boolean {
    return !!(GetCurrentSubmissionAccountingSystem(state)) &&
        _getCurrentSubmission(state)?.submissionState === SubmissionState.requestCreated;
}

export function GetCurrentSubmissionAccountingSystem(state: ApplicationState): string | null {
    return _getCurrentSubmission(state)?.accountingPackage
}

export function GetExistingConnectionForCurrentSubmission(state: ApplicationState): ConnectionDescriptor | null | undefined {
    const current = _getCurrentSubmission(state);
    if (!current) return undefined;
    if (current.existingConnectionLookupState === ExistingConnectionLookupState.pending) return undefined;
    return current.existingConnection;
}

function _getCurrentSubmissionDetails(state: ApplicationState): Partial<SubmissionDetail> {
    return state?.newDataCollectionParameters?.currentSubmission?.submission
}

export function GetCurrentSubmissionId(state: ApplicationState): string {
    return _getCurrentSubmissionDetails(state)?.id
}

export function GetAccountingSystemConnectionState(state: ApplicationState): AccountingSystemConnectionState {
    return _getCurrentSubmission(state)?.accountingSystemConnectionState ?? AccountingSystemConnectionState.idle;
}

export function GetAccountingSystemConnectionError(state: ApplicationState): IConnectionError | undefined {
    return _getCurrentSubmission(state)?.connectionError;
}

export function GetConnectEndpoint(state: ApplicationState): string | null {
    return _getCurrentSubmission(state)?.connectEndpoint ?? null;
}

export function IsConnectingToAccountingSystem(state: ApplicationState): boolean {
    return GetAccountingSystemConnectionState(state) === AccountingSystemConnectionState.connecting;
}

export function IsConnectedToAccountingSystem(state: ApplicationState): boolean {
    return GetAccountingSystemConnectionState(state) === AccountingSystemConnectionState.connected;
}

export function IsAccountingSystemConnectionInTerminalState(state: ApplicationState): boolean {
    return GetAccountingSystemConnectionState(state) === AccountingSystemConnectionState.connected ||
        GetAccountingSystemConnectionState(state) === AccountingSystemConnectionState.error;
}

/**
 * The single view FinancialsImportV2 should render for the current store state. Every reachable
 * (submissionState x accountingSystemConnectionState) combination maps to exactly one of these, so the
 * component never overlaps modals and never shows a blank screen while the flow is processing.
 *
 * - 'none'                 — no flow in progress; only the import wizard is shown.
 * - 'working'              — generic spinner for any network wait / transient in-flight state.
 * - 'waitingOnConnection'  — the OAuth popup is open and we are establishing the connection.
 * - 'selectAccountingSystem' / 'shareLink' / 'confirmConnect' — states that wait on the user.
 * - 'error'                — a dismissible error modal.
 */
export type FinancialsImportFlowView =
    | 'none'
    | 'working'
    | 'waitingOnConnection'
    | 'selectAccountingSystem'
    | 'shareLink'
    | 'confirmConnect'
    | 'error';

export function GetFinancialsImportFlowView(state: ApplicationState): FinancialsImportFlowView {
    const current = _getCurrentSubmission(state);
    if (!current) {
        return 'none';
    }

    const conn = current.accountingSystemConnectionState ?? AccountingSystemConnectionState.idle;

    // Checked first because these connection outcomes can occur across multiple submissionStates
    // (e.g. a popup block reports an error while still in requestCreated).
    if (conn === AccountingSystemConnectionState.error) {
        return 'error';
    }
    if (conn === AccountingSystemConnectionState.aborted) {
        // The user closed the popup without finishing. If a package is still selected, drop back to the
        // confirmation modal so they can Connect again (retry) or Cancel (abort the whole flow). If they
        // cleared it via Back (SetSelectedAccountingPackage(null)), return them to the accounting-system
        // selector. conn stays 'aborted' here — that is what distinguishes this post-abort decision phase
        // from the transient submissionCreated + idle state; the next Connect dispatches 'connecting'.
        return current.accountingPackage ? 'confirmConnect' : 'selectAccountingSystem';
    }

    switch (current.submissionState) {
        case SubmissionState.creatingRequest:
            return 'working';
        case SubmissionState.requestError:
            return 'error';
        case SubmissionState.requestCreated:
            if (current.submissionRequest?.hasShareableLink) {
                return 'shareLink';
            }
            // The universal collector ("upload files yourself") auto-connects: it is never chosen from the
            // package picker and never shows the reuse/confirm modal (see
            // CreateUniversalConnectorSubmissionAndStartImport). Hold on the 'working' spinner while that
            // thunk drives request → package → submission → connect, instead of flashing the lender-only
            // 'selectAccountingSystem' picker or the 'confirmConnect' modal.
            if (current.dataCollectionSubmissionRequest.connectorType === ConnectorType.universalConnector) {
                return 'working';
            }
            if (!current.accountingPackage) {
                return 'selectAccountingSystem';
            }
            // A package is selected; wait on the existing-connection lookup before showing the confirmation
            // modal so it can render the correct variant (reuse-or-reconnect vs plain connect).
            if (current.existingConnectionLookupState === ExistingConnectionLookupState.pending) {
                return 'working';
            }
            return 'confirmConnect';
        case SubmissionState.creatingSubmission:
            return 'working';
        case SubmissionState.submissionError:
            return 'error';
        case SubmissionState.submissionCreated:
            // conn is already narrowed to idle | connecting | connected (error/aborted handled above).
            switch (conn) {
                case AccountingSystemConnectionState.idle:
                    // Transient: connecting is dispatched immediately when the connect step begins.
                    return 'working';
                case AccountingSystemConnectionState.connecting:
                    return 'waitingOnConnection';
                case AccountingSystemConnectionState.connected:
                    // Transient: CompleteCurrentSubmission tears the flow down right after success.
                    return 'working';
                default: {
                    const _exhaustive: never = conn;
                    return _exhaustive;
                }
            }
        default: {
            const _exhaustive: never = current.submissionState;
            return _exhaustive;
        }
    }
}